import { AddPhotoAlternateOutlined, DeleteOutline } from '@mui/icons-material'
import { Box, Grid, Typography } from '@mui/material'
import { Form } from 'components/form'
import { useAlert } from 'contexts/alert'
import React, {
  ChangeEvent,
  DragEvent,
  Fragment,
  MutableRefObject,
  useCallback,
  useRef,
  useState,
} from 'react'
import { UseFormReturn } from 'react-hook-form'
import ModalCropTheme from '../../form/modal-crop'
import { useStyles } from './styles'

type FileType = 'principal' | 'branco' | 'preto'

interface ILogoUploadProps {
  formData: UseFormReturn<any>
}

const LogoAndColors: React.FC<ILogoUploadProps> = ({
  formData: {
    watch,
    control,
    setValue,
    register,
    formState: { errors },
  },
}) => {
  const [modalOpen, setModalOpen] = useState<{
    baseLogoOpen: boolean
    whiteLogoOpen: boolean
    blackLogoOpen: boolean
  }>({
    baseLogoOpen: false,
    whiteLogoOpen: false,
    blackLogoOpen: false,
  })

  const classes = useStyles()

  const { closeAlert } = useAlert()
  const principalLogoRef = useRef<HTMLInputElement>(null)
  const whiteLogoRef = useRef<HTMLInputElement>(null)
  const blackLogoRef = useRef<HTMLInputElement>(null)

  const createUrlPreview = (file: File, name: string) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      setValue(name, e.target!.result)
    }

    reader.readAsDataURL(file)
  }

  const handleAddFile = (e: ChangeEvent<HTMLInputElement>, type: FileType) => {
    const filesFromForm = e.target.files

    if (filesFromForm) {
      if (type === 'principal') {
        createUrlPreview(filesFromForm[0], 'baseLogo')
        setValue('baseLogoFile', filesFromForm[0])
        setModalOpen((prev) => ({ ...prev, baseLogoOpen: true }))
      } else if (type === 'branco') {
        createUrlPreview(filesFromForm[0], 'whiteLogo')
        setValue('whiteLogoFile', filesFromForm[0])
        setModalOpen((prev) => ({ ...prev, whiteLogoOpen: true }))
      } else if (type === 'preto') {
        createUrlPreview(filesFromForm[0], 'blackLogo')
        setValue('blackLogoFile', filesFromForm[0])
        setModalOpen((prev) => ({ ...prev, blackLogoOpen: true }))
      }
    }
  }

  const handleRemoveFile = (type: FileType) => {
    if (type === 'principal') {
      setValue('baseLogoFile', null)
      setValue('baseLogo', null)
      principalLogoRef.current!.value = ''
    } else if (type === 'branco') {
      setValue('whiteLogoFile', null)
      setValue('whiteLogo', null)
      whiteLogoRef.current!.value = ''
    } else if (type === 'preto') {
      setValue('blackLogoFile', null)
      setValue('blackLogo', null)
      blackLogoRef.current!.value = ''
    }
  }

  const handleFileSelect = (e: DragEvent<HTMLDivElement>, type: FileType) => {
    e.preventDefault()
    const files: FileList = e.dataTransfer.files

    if (type === 'principal') {
      createUrlPreview(files[0], 'baseLogo')
      setValue('baseLogoFile', files[0])
      setModalOpen((prev) => ({ ...prev, baseLogoOpen: true }))
    } else if (type === 'branco') {
      createUrlPreview(files[0], 'whiteLogo')
      setValue('whiteLogoFile', files[0])
      setModalOpen((prev) => ({ ...prev, whiteLogoOpen: true }))
    } else if (type === 'preto') {
      createUrlPreview(files[0], 'blackLogo')
      setValue('blackLogoFile', files[0])
      setModalOpen((prev) => ({ ...prev, blackLogoOpen: true }))
    }
  }

  const handleDragOver = useCallback((e: any) => {
    e.preventDefault()
  }, [])

  const handleChange = (formField: any) => closeAlert()

  const handleFileInputClick = (type: FileType) => {
    if (type === 'principal') {
      principalLogoRef.current?.click()
    } else if (type === 'branco') {
      whiteLogoRef.current?.click()
    } else if (type === 'preto') {
      blackLogoRef.current?.click()
    }
  }

  return (
    <>
      <Typography variant='h6'>
        Faça o upload de 3 logos de sua empresa na seguinte ordem, padrão, todo branco e todo preto, com no
        minimo 100x100
      </Typography>

      <Box sx={{ width: '100%' }}>
        <Typography
          variant='h6'
          sx={{ margin: '1rem 0' }}
        >
          Logos
        </Typography>
        <Grid
          container
          spacing={2}
        >
          <Grid
            item
            xs={4}
          >
            <UploadBox
              type='principal'
              preview={watch('baseLogo')}
              reference={principalLogoRef as any}
              onAddFile={handleAddFile}
              onDrop={handleFileSelect}
              onDragOver={handleDragOver}
              onRemoveFile={handleRemoveFile}
              onFileInputClick={handleFileInputClick}
            />
          </Grid>
          <Grid
            item
            xs={4}
          >
            <UploadBox
              type='branco'
              preview={watch('whiteLogo')}
              reference={whiteLogoRef as any}
              onAddFile={handleAddFile}
              onDrop={handleFileSelect}
              onDragOver={handleDragOver}
              onRemoveFile={handleRemoveFile}
              onFileInputClick={handleFileInputClick}
            />
          </Grid>
          <Grid
            item
            xs={4}
          >
            <UploadBox
              type='preto'
              preview={watch('blackLogo')}
              reference={blackLogoRef as any}
              onAddFile={handleAddFile}
              onDrop={handleFileSelect}
              onDragOver={handleDragOver}
              onRemoveFile={handleRemoveFile}
              onFileInputClick={handleFileInputClick}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ width: '100%' }}>
        <Typography
          variant='h6'
          sx={{ margin: '1rem 0' }}
        >
          Cores
        </Typography>
        <Box className={classes.formBoxColor}>
          <Form.Input
            fullWidth
            type='color'
            label='Cor Primaria'
            error={!!errors.primaryColor}
            errorMessage={errors?.primaryColor?.message as string}
            disabled={false}
            sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
            maxLength={255}
            name='primaryColor'
            rules={{ onChange: (e) => handleChange(e) }}
            register={register}
            control={control}
          />

          <Form.Input
            fullWidth
            type='color'
            label='Cor Segundaria'
            error={!!errors.secondaryColor}
            errorMessage={errors?.secondaryColor?.message as string}
            disabled={false}
            sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
            maxLength={255}
            name='secondaryColor'
            rules={{ onChange: (e) => handleChange(e) }}
            register={register}
            control={control}
          />

          <Form.Input
            fullWidth
            type='color'
            label='Cor Terciária'
            error={!!errors.terciaryColor}
            errorMessage={errors?.terciaryColor?.message as string}
            disabled={false}
            sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
            maxLength={255}
            name='terciaryColor'
            rules={{ onChange: (e) => handleChange(e) }}
            register={register}
            control={control}
          />
        </Box>

        <Grid
          container
          className={classes.itemBox}
        >
          <Grid
            item
            xs={4}
          >
            <Box
              className={classes.colorBox}
              bgcolor={`#${watch('primaryColor')}`}
            />
            <span>Primaria</span>
          </Grid>
          <Grid
            item
            xs={4}
          >
            <Box
              className={classes.colorBox}
              bgcolor={`#${watch('secondaryColor')}`}
            />
            <span>Secundaria</span>
          </Grid>
          <Grid
            item
            xs={4}
          >
            <Box
              className={classes.colorBox}
              bgcolor={`#${watch('terciaryColor')}`}
            />
            <span>Terciária</span>
          </Grid>
        </Grid>
      </Box>

      <ModalCropTheme
        file={watch('baseLogoFile')}
        open={modalOpen.baseLogoOpen}
        onClose={() => setModalOpen((prev) => ({ ...prev, baseLogoOpen: false }))}
        onCrop={(file) => {
          createUrlPreview(file, 'baseLogo')
          setValue('baseLogoFile', file)
        }}
      />

      <ModalCropTheme
        file={watch('whiteLogoFile')}
        open={modalOpen.whiteLogoOpen}
        onClose={() => setModalOpen((prev) => ({ ...prev, whiteLogoOpen: false }))}
        onCrop={(file) => {
          createUrlPreview(file, 'whiteLogo')
          setValue('whiteLogoFile', file)
        }}
      />

      <ModalCropTheme
        file={watch('blackLogoFile')}
        open={modalOpen.blackLogoOpen}
        onClose={() => setModalOpen((prev) => ({ ...prev, blackLogoOpen: false }))}
        onCrop={(file) => {
          createUrlPreview(file, 'blackLogo')
          setValue('blackLogoFile', file)
        }}
      />
    </>
  )
}

interface UploadBoxProps {
  type: FileType
  preview: string
  reference: MutableRefObject<HTMLInputElement>
  onFileInputClick: (type: FileType) => void
  onDrop: (e: DragEvent<HTMLDivElement>, type: FileType) => void
  onDragOver: (e: DragEvent<HTMLDivElement>) => void
  onAddFile: (e: ChangeEvent<HTMLInputElement>, type: FileType) => void
  onRemoveFile: (type: FileType) => void
}

const UploadBox: React.FC<UploadBoxProps> = ({
  onFileInputClick,
  onRemoveFile,
  onDragOver,
  onAddFile,
  reference,
  preview,
  onDrop,
  type,
}) => {
  const classes = useStyles()

  const havePreviewClassName = (preview: null | string, type: FileType) => {
    if (preview == null) {
      return classes.formBox
    }

    if (type === 'branco') {
      return classes.formBoxWhite
    }

    return classes.formBoxBase
  }

  const handleFileInputClick = () => {
    if (preview == null) {
      onFileInputClick(type)
    }
  }

  return (
    <Box
      sx={{ cursor: preview == null ? 'pointer' : 'default' }}
      className={havePreviewClassName(preview, type)}
      onDrop={(e) => onDrop(e, type)}
      onDragOver={onDragOver}
      onClick={handleFileInputClick}
    >
      <input
        name='file'
        hidden
        type='file'
        multiple={false}
        ref={reference}
        onChange={(e) => onAddFile(e, type)}
      />
      {preview != null ? (
        <>
          <img
            src={preview}
            alt='logo'
          />
          <span
            className={classes.deleteButton}
            onClick={() => onRemoveFile(type)}
          >
            <DeleteOutline />
          </span>
        </>
      ) : (
        <Box className={classes.boxText}>
          <AddPhotoAlternateOutlined fontSize='large' />
          <span>Arrastar e Solte o logo {type} aqui</span>
        </Box>
      )}
    </Box>
  )
}

export default LogoAndColors
